import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import MuiTypography from "@mui/material/Typography";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { cleanProps } from "../internal/cleanProps";
import { fieldErrorSx, fieldHelpSx, fieldLabelSx } from "../internal/field-styles";
import { tokens } from "../tokens";
import type { DatePickerProps } from "../types";

/**
 * Containment note (types.ts): upstream's date-library generic (`TDate`)
 * is fixed to dayjs's `Dayjs` permanently. Requires a `LocalizationProvider`
 * ancestor with the dayjs adapter — `components/ThemeProvider.tsx` supplies
 * one; this component does not mount its own.
 *
 * ADR-20261102-h2fd: the H2 design system's DatePicker inside its FormField -- the label in bold
 * above, the help text between the label and the control, the error message below.
 */
export function DatePicker({
  id,
  label,
  value,
  onChange,
  help,
  error,
  errorText,
  required,
  disabled,
  minDate,
  maxDate,
  name,
  testId,
}: Readonly<DatePickerProps>) {
  const helpId = help !== undefined ? `${id}-help` : undefined;
  const errorId = error === true && errorText !== undefined ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter((part): part is string => part !== undefined).join(" ") || undefined;

  const textFieldSlotProps: Record<string, unknown> = cleanProps({
    id,
    name,
    required,
    error,
    fullWidth: true,
    "data-testid": testId,
    inputProps: cleanProps({ "aria-describedby": describedBy }),
  });

  return (
    <FormControl fullWidth sx={{ mb: `${tokens.spacing.lg}px` }} {...cleanProps({ error, required, disabled })}>
      <FormLabel htmlFor={id} sx={fieldLabelSx}>
        {label}
      </FormLabel>
      {help !== undefined ? (
        <MuiTypography id={helpId} component="p" variant="caption" sx={fieldHelpSx}>
          {help}
        </MuiTypography>
      ) : null}
      <MuiDatePicker
        // H2's DatePicker renders a single text input, which the field's label names.
        enableAccessibleFieldDOMStructure={false}
        value={value}
        onChange={onChange}
        {...cleanProps({ minDate, maxDate, disabled })}
        slotProps={{ textField: textFieldSlotProps }}
      />
      {errorId !== undefined ? (
        <MuiTypography id={errorId} component="p" variant="caption" sx={fieldErrorSx}>
          {errorText}
        </MuiTypography>
      ) : null}
    </FormControl>
  );
}
